import type { ITenant } from '@experiences/interfaces';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApLink,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import type {
    Control,
    ErrorOption,
    FieldPath,
    FieldPathValue,
    SetValueConfig,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

import { useDocumentationLinks } from '../../../../common/hooks/useDocumentationLink';
import type {
    ApiFlavor,
    ByoLlmConfiguration,
    ByoOperationGroupConfiguration,
    LlmConnectorType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import { ByoFormErrorTypes } from '../../../../services/llm-gateway/LlmGateway.types';
import {
    getFeatureDescriptionTranslationKey,
    translationKeyExists,
} from '../../../../services/llm-gateway/LlmGateway.utils';
import type { Folder } from '../../../../services/orchestrator/OrchFolderService';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';
import { AddEditConfigurationHeaderSection } from './AddEditConfigurationHeaderSection';
import { AllModelsConfigurationSection } from './AllModelsConfigurationSection';
import { AnyModelConfigurationSection } from './AnyModelConfigurationSection';
import { FolderDropdown } from './FolderDropdown';

export interface ByoConfigurationFormContentProps {
    organizationId: string;
    control: Control<AddEditConfigurationFormData>;
    resetField: (name: FieldPath<AddEditConfigurationFormData>) => void;
    setValue:
    (name: FieldPath<AddEditConfigurationFormData>, value: FieldPathValue<AddEditConfigurationFormData, FieldPath<AddEditConfigurationFormData>>, options?: SetValueConfig) => void;
    setError: (name: (FieldPath<AddEditConfigurationFormData> | `root.${string}` | 'root'), error: ErrorOption, options?: { shouldFocus: boolean }) => void;
    product?: string;
    feature?: string;
    folderKey?: string | null;
    featureHasAllModelsConfigurationConstraint: boolean;
    productConfigurations?: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    llmToConnectorTypeMapping: Record<string, LlmConnectorType[]>;
    vendorLlmApiFlavors?: Record<string, ApiFlavor[] | null>;
    availableLlms?: Record<string, Record<string, string[]>>;
    notifyFieldChanges: () => void;
    onFolderChange: (newFolder?: Folder) => void;
    validateLlmConfigurationsModelNameOverlap: (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => Promise<string | true>;
    validateLlmConfigurationsRequiredFields: (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => Promise<string | true>;
    isEditMode?: boolean;
    tenants?: ITenant[];
    onTenantChange?: (tenantId: string) => void;
}

export const ByoConfigurationFormContent: React.FC<ByoConfigurationFormContentProps> = ({
    organizationId,
    control,
    resetField,
    setValue,
    setError,
    product,
    feature,
    folderKey,
    featureHasAllModelsConfigurationConstraint,
    productConfigurations,
    llmToConnectorTypeMapping,
    vendorLlmApiFlavors,
    availableLlms,
    notifyFieldChanges,
    onFolderChange,
    validateLlmConfigurationsModelNameOverlap,
    validateLlmConfigurationsRequiredFields,
    isEditMode,
    tenants,
    onTenantChange,
}) => {
    const [ tenantId, setTenantId ] = useState<string>('');
    // Auto-select the sole tenant once the tenants prop arrives (SWR-driven, may be empty on first render).
    useEffect(() => {
        if (!tenantId && tenants?.length === 1) {
            setTenantId(tenants[0].id);
        }
    }, [ tenantId, tenants ]);
    const tenant = tenants?.find(t => t.id === tenantId);

    const handleTenantChange = useCallback((newTenantId: string) => {
        setTenantId(newTenantId);
        onTenantChange?.(newTenantId);
    }, [ onTenantChange ]);
    const {
        formatMessage: translate, messages,
    } = useIntl();
    const getLocalizedLink = useDocumentationLinks();
    const documentationLink = getLocalizedLink({ articleSlug: '/admin-guide/configuring-llms' });

    const showFeatureDescription = (() => {
        if (!product || !feature) {
            return false;
        }
        const featureDescKey = getFeatureDescriptionTranslationKey(product, feature);
        return translationKeyExists(messages, featureDescKey);
    })();

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.L}>
            <AddEditConfigurationHeaderSection
                control={control}
                resetField={resetField}
                setValue={setValue}
                setError={setError}
                product={product}
                notifyFieldChanges={notifyFieldChanges}
                productConfigurations={productConfigurations ?? {}}
                replaceableLlms={availableLlms ?? {}}
                isEditMode={isEditMode}
                tenants={tenants}
                onTenantChange={handleTenantChange}
            />
            {product && feature && (
                <>
                    <ApAlertBar
                        status="info"
                        cancelable
                        style={{ maxWidth: '1000px' }}
                    >
                        <FormattedMessage
                            id='CLIENT_BYO_LLM_CONFIGURE_FEATURE_DESCRIPTION'
                            values={{
                                documentationLink: (msg: React.ReactNode[]) =>
                                    (
                                        <ApLink
                                            href={documentationLink}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {msg}
                                        </ApLink>
                                    ),
                            }}
                        />
                    </ApAlertBar>
                    <Controller
                        name="folderKey"
                        control={control}
                        rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
                        render={({ field }) => (
                            <FolderDropdown
                                tenant={tenant}
                                value={field.value ?? undefined}
                                required
                                onChange={(newValue) => {
                                    field.onChange(newValue?.Key ?? null);
                                    onFolderChange(newValue);
                                }}
                                labelTooltip={{
                                    translationKey: 'CLIENT_BYO_LLM_FOLDER_TOOLTIP',
                                    placement: 'right',
                                }} />
                        )}
                    />
                    <UiStack
                        direction="column"
                        gap={SpacingToken.XS}
                        mt={SpacingToken.M}
                        mb={SpacingToken.S}>
                        <UiText variant={FontVariantToken.fontSizeLBold}>
                            {translate({ id: 'CLIENT_BYO_LLM_MODEL_CONFIGURATION' })}
                        </UiText>
                        {showFeatureDescription && (
                            <UiText>
                                {translate({ id: getFeatureDescriptionTranslationKey(product, feature) })}
                            </UiText>
                        )}
                    </UiStack>
                    <Controller
                        name="llmConfigurations"
                        control={control}
                        rules={{
                            required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }),
                            validate: {
                                [ByoFormErrorTypes.ModelNameOverlap]: validateLlmConfigurationsModelNameOverlap,
                                [ByoFormErrorTypes.RequiredField]: validateLlmConfigurationsRequiredFields,
                            },
                        }}
                        render={({
                            field, fieldState,
                        }) =>
                            featureHasAllModelsConfigurationConstraint
                                ? <AllModelsConfigurationSection
                                    organizationId={organizationId}
                                    tenantId={tenantId}
                                    folderKey={folderKey ?? undefined}
                                    llmConfigurationsField={field}
                                    notifyFieldChanges={notifyFieldChanges}
                                    llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                    vendorLlmApiFlavors={vendorLlmApiFlavors}
                                    product={product}
                                    feature={feature}
                                    productConfigurations={productConfigurations}
                                    error={fieldState.error}
                                />
                                : <AnyModelConfigurationSection
                                    organizationId={organizationId}
                                    tenantId={tenantId}
                                    folderKey={folderKey ?? undefined}
                                    modelsConfigurationOption={productConfigurations?.[product]?.[feature]?.modelsConfigurationOption}
                                    llmConfigurationsField={field}
                                    notifyFieldChanges={notifyFieldChanges}
                                    llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                    vendorLlmApiFlavors={vendorLlmApiFlavors}
                                    replaceableLlms={availableLlms?.[product]?.[feature] ?? []}
                                    product={product}
                                    feature={feature}
                                    productConfigurations={productConfigurations}
                                    error={fieldState.error}
                                />}
                    />
                </>
            )}
        </UiStack>
    );
};
